import { LogOut } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../../app/auth';
import { usePrefs, type LocaleChoice, type ThemeMode } from '../../app/prefs';
import type { Role } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import './settings.css';

// The signed-in person, how the app looks and speaks in this browser, and signing out (the mobile
// app's settings screen). The app lock and the server address belong to the phone app only.

export function SettingsPage() {
  const t = useT();
  const { session, signOut } = useAuth();
  const { theme, setTheme, localeChoice, setLocale } = usePrefs();
  const [signingOut, setSigningOut] = useState(false);

  const roles: Record<Role, string> = { owner: t('roleOwner'), approver: t('roleApprover'), viewer: t('roleViewer') };

  return (
    <div className="page settings">
      <h1 className="display-small">{t('navSettings')}</h1>

      {session && (
        <div className="card settings-who">
          <span className="settings-initial" aria-hidden>
            {[...session.name.trim()][0]?.toUpperCase()}
          </span>
          <div className="settings-who-text">
            <p className="title-medium">{session.name}</p>
            <p className="body-small">{session.email}</p>
            <p className="label-small settings-org">
              {session.organizationName} · {roles[session.role]}
            </p>
          </div>
        </div>
      )}

      <Group title={t('settingsAppearance')}>
        <Choice<ThemeMode>
          name="theme"
          value={theme}
          onChange={setTheme}
          options={[
            { value: 'system', label: t('themeSystem') },
            { value: 'light', label: t('themeLight') },
            { value: 'dark', label: t('themeDark') },
          ]}
        />
      </Group>

      <Group title={t('settingsLanguage')}>
        <Choice<LocaleChoice>
          name="language"
          value={localeChoice}
          onChange={setLocale}
          options={[
            { value: 'system', label: t('languageSystem') },
            // Each language in its own words, as in the mobile app.
            { value: 'lt', label: 'Lietuvių', lang: 'lt' },
            { value: 'en', label: 'English', lang: 'en' },
          ]}
        />
      </Group>

      <button
        type="button"
        className="btn btn-outlined btn-danger btn-block settings-signout"
        disabled={signingOut}
        onClick={() => {
          setSigningOut(true);
          void signOut().finally(() => setSigningOut(false));
        }}
      >
        <LogOut size={20} aria-hidden />
        {t('signOut')}
      </button>
    </div>
  );
}

/** A titled group of settings, as the mobile app's cards under a small heading. */
function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="settings-group">
      <legend className="overline">{title}</legend>
      <div className="card settings-card">{children}</div>
    </fieldset>
  );
}

/** One of a few, side by side (the mobile app's segmented buttons), as radio buttons: arrow keys move
 * between them. */
function Choice<T extends string>({
  name,
  value,
  onChange,
  options,
}: {
  name: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; lang?: string }[];
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <label key={o.value} className="segment" lang={o.lang}>
          <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}
